import React, { useEffect, useState } from "react";
import {
    Button,
    Cascader,
    DatePicker,
    Form,
    Input,
    InputNumber,
    Row,
    Space,
    Select,
    Table,
    Checkbox,
    Radio,
} from "antd";
import moment from "moment";
import "moment/locale/zh-cn";
import "./index.less";
import event from "@/util/event";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { getConfig, isValid } from "../utils";
import UploadFile from "./upload-file";
import { FormPatternTypes } from "../core/type";
import { InputSelect } from "./input-select";
import { formatMomentValue } from "../core/__builtins__/moment";
import { connect, mapCopy, mapReadPretty } from "../core/connect";
import { PreviewUpload } from "./preview/upload-file";

const { TextArea } = Input;
const { Option, OptGroup } = Select;

function useMapReadPretty({ pattern, children, component, componentProps = {} }) {
    if (pattern === FormPatternTypes.readPretty) {
        return React.createElement(component, { ...componentProps });
    }
    return React.cloneElement(children);
}

function TextInput({ item, infoEdit, field, record, parentFormItemLayout, layout, formId }) {
    let config = getConfig(item, infoEdit, parentFormItemLayout, layout);
    let {
        rules,
        placeholder,
        disabled,
        label,
        maxLength,
        formItemLayout,
        autoComplete,
        style,
        pattern,
        extra,
        validateTrigger,
    } = config;
    const input = useMapReadPretty({
        pattern: pattern,
        children: (
            <Input
                disabled={disabled}
                autoComplete={autoComplete || "off"}
                maxLength={maxLength}
                placeholder={placeholder || `请输入${label}`}
                onChange={e => {
                    let value = e.target.value.trim();
                    if (record) {
                        record[item.name] = value;
                    }
                    event.emit("inputChange", { value: value, name: item.name, record: record, formId });
                }}
            />
        ),
        component: FormText,
    });
    return (
        <Form.Item
            style={style}
            {...field}
            {...formItemLayout}
            label={label}
            fieldKey={getFieldKey(item, field)}
            name={getFieldKey(item, field)}
            rules={rules}
            extra={extra}
            validateTrigger={validateTrigger}
            tooltip={item.tooltip}>
            {input}
        </Form.Item>
    );
}

const FormText = ({ value }) => {
    return <span>{value}</span>;
};

const FormTextArea = ({ value }) => {
    if (!value) {
        return <span>-</span>;
    }

    // 处理多行文本，保留换行符和空格
    const formatText = text => {
        return String(text)
            .split("\n")
            .map((line, index, array) => (
                <React.Fragment key={index}>
                    {line}
                    {index < array.length - 1 && <br />}
                </React.Fragment>
            ));
    };

    return (
        <span style={{ whiteSpace: "pre-wrap", wordBreak: "break-word", display: "inline-block", paddingTop: 5 }}>
            {formatText(value)}
        </span>
    );
};

const PreviewSelect = ({ value, dataSource, mode, labelKey, valueKey }) => {
    const getSelected = () => {
        if (mode === "multiple" || mode === "tags") {
            return Array.isArray(value) ? value : [];
        } else {
            return isValid(value) ? [value] : [];
        }
    };
    const getLabel = target => {
        let find = dataSource?.find(item => {
            return item[valueKey] === target;
        });
        return find?.[labelKey];
    };
    const getLabels = () => {
        const selected = getSelected();
        if (selected.length === 1) return getLabel(selected[0]);
        return selected.map((item, key) => {
            return <span key={key}>{getLabel(item)}</span>;
        });
    };
    return <span>{getLabels()}</span>;
};
const FormRender = ({ name, value, item, disabled, formId }) => {
    return item.render ? item.render(item, disabled, value, name, formId) : <span>{value}</span>;
};

function Text({ item, infoEdit, field, parentFormItemLayout, layout }) {
    let config = getConfig(item, infoEdit, parentFormItemLayout, layout);
    let { label, style, formItemLayout, extra, validateTrigger } = config;

    return (
        <Form.Item
            style={style}
            {...field}
            {...formItemLayout}
            label={label}
            fieldKey={getFieldKey(item, field)}
            name={getFieldKey(item, field)}
            extra={extra}
            validateTrigger={validateTrigger}
            tooltip={item.tooltip}>
            <FormText />
        </Form.Item>
    );
}

function CommonSelect({ item, infoEdit, field, record, selectList, parentFormItemLayout, layout, formId, form }) {
    let [refresh, setRefresh] = useState(0);
    let config = getConfig(item, infoEdit, parentFormItemLayout, layout);
    let {
        rules,
        idKey,
        valueKey,
        showSearch,
        mode,
        onSearch,
        allowClear,
        disabled,
        together,
        style,
        formItemLayout,
        extra,
        validateTrigger,
    } = config;
    let optionKey = valueKey ? valueKey : "name";
    let optionValue = idKey ? idKey : "id";

    const selectView = connect(
        <Select
            // multiple | tags
            mode={mode}
            allowClear={allowClear || false}
            showSearch={showSearch || false}
            onSearch={showSearch && (value => onSearch && onSearch(value, item))}
            filterOption={(input, option) => option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}
            onChange={(value, option) => {
                if (record) {
                    record[item.name] = value;
                }
                event.emit("singlesSelectChange", { value: option, name: item.name, record: record, formId });
                setRefresh(++refresh);
            }}
            validateTrigger={validateTrigger}
            disabled={disabled}>
            {(selectList || item.list || []).map((it, index) => {
                return (
                    <Option key={index} value={it[optionValue]} data={it}>
                        {together ? `${it.id}:${it[optionKey]}` : it[optionKey]}
                    </Option>
                );
            })}
        </Select>,
        config,
        mapReadPretty({
            component: PreviewSelect,
            componentProps: {
                labelKey: optionKey,
                valueKey: optionValue,
                mode: mode,
                dataSource: selectList || item.list || [],
            },
        }),
        mapCopy(() => {
            let name = getFieldKey(item, field);
            return form.getFieldValue(name);
        }),
    );
    return (
        <Form.Item
            style={style}
            {...field}
            label={item.label}
            {...formItemLayout}
            fieldKey={getFieldKey(item, field)}
            name={getFieldKey(item, field)}
            rules={rules}
            extra={extra}
            validateTrigger={validateTrigger}
            tooltip={item.tooltip}>
            {selectView}
        </Form.Item>
    );
}

function GroupSelect({ item, infoEdit, parentFormItemLayout, layout, formId }) {
    let config = getConfig(item, infoEdit, parentFormItemLayout, layout);
    let { rules, disabled, formItemLayout, extra, validateTrigger } = config;

    // 查找对应选项的desc
    function selectFromOrigin(value) {
        let desc = "";
        item.list.map(obj => {
            obj.group.map(inner => {
                if (inner.id === value) {
                    desc = inner.desc;
                }
            });
        });
        return desc;
    }

    const handleChange = value => {
        item.value = value;
        const desc = selectFromOrigin(value);
    };
    return (
        <Form.Item
            label={item.label}
            name={item.name}
            {...formItemLayout}
            rules={rules}
            extra={extra}
            validateTrigger={validateTrigger}
            tooltip={item.tooltip}>
            <Select onChange={handleChange} disabled={disabled}>
                {(item.list || []).map(group => (
                    <OptGroup label={group.name} key={group.id}>
                        {group.group &&
                            group.group.map(child => (
                                <Option value={child.id} key={child.id}>
                                    {child.name}
                                </Option>
                            ))}
                    </OptGroup>
                ))}
            </Select>
        </Form.Item>
    );
}

function Textarea({ item, infoEdit, field, parentFormItemLayout, layout }) {
    let config = getConfig(item, infoEdit, parentFormItemLayout, layout);
    let { rules, placeholder, disabled, style, formItemLayout, extra, pattern } = config;
    const input = useMapReadPretty({
        pattern: pattern,
        children: (
            <TextArea
                className={"form-control"}
                disabled={disabled}
                placeholder={placeholder || "请输入" + item.label}
            />
        ),
        component: FormTextArea,
    });
    return (
        <Form.Item
            style={style}
            label={item.label}
            fieldKey={getFieldKey(item, field)}
            name={getFieldKey(item, field)}
            {...formItemLayout}
            rules={rules}
            extra={extra}
            tooltip={item.tooltip}>
            {input}
        </Form.Item>
    );
}

const PreviewDateInput = ({ value, format, placeholder }) => {
    const getLabels = () => {
        const labels = formatMomentValue(value, format, placeholder);
        return Array.isArray(labels) ? labels.join("~") : labels;
    };
    return <span>{getLabels()}</span>;
};

function DateInput({ item, infoEdit, field, parentFormItemLayout, layout, formId }) {
    let config = getConfig(item, infoEdit, parentFormItemLayout, layout);
    let {
        rules,
        disableCurrent,
        format,
        defaultValue,
        isRange,
        showTime,
        picker,
        disabled,
        style,
        formItemLayout,
        pattern,
        extra,
        validateTrigger,
    } = config;
    const date = new Date();
    const hours = date.getHours(); //时，
    const minutes = date.getMinutes(); //分
    const seconds = date.getSeconds(); //秒
    const disableData = disableCurrent || item.disableCurrent ? { disabledDate, disabledTime } : {};

    function range(start, end) {
        const result = [];
        for (let i = start; i < end; i++) {
            result.push(i);
        }
        return result;
    }

    function disabledDate(current) {
        // Can not select days before today
        return current && current <= moment().startOf("day");
    }

    function disabledTime() {
        const date = new Date();
        const hours = date.getHours(); //时，
        const minutes = date.getMinutes(); //分
        const seconds = date.getSeconds(); //秒

        return {
            disabledHours: () => range(0, hours),
            disabledMinutes: () => range(0, minutes),
            disabledSeconds: () => range(0, seconds),
        };
    }

    if (showTime === undefined) {
        showTime = true;
    }
    showTime = showTime ? { defaultValue: moment(`${hours}:${minutes}:${seconds}`, "HH:mm:ss") } : false;
    const dateInput = useMapReadPretty({
        pattern: pattern,
        children: isRange ? (
            <DatePicker.RangePicker
                style={{ width: "100%" }}
                disabled={disabled}
                showTime={showTime}
                isRange={isRange}
                defaultValue={defaultValue}
            />
        ) : (
            <DatePicker
                style={{ width: "100%" }}
                {...disableData}
                disabled={disabled}
                format={item.format || format}
                defaultValue={defaultValue}
                showTime={showTime}
            />
        ),
        component: PreviewDateInput,
        componentProps: {
            format: item.format,
            placeholder: "",
        },
    });
    return (
        <Form.Item
            style={style}
            label={item.label}
            fieldKey={getFieldKey(item, field)}
            name={getFieldKey(item, field)}
            shouldUpdate={true}
            rules={rules}
            picker={picker}
            initialValue={defaultValue}
            {...formItemLayout}
            extra={extra}
            validateTrigger={validateTrigger}
            tooltip={item.tooltip}>
            {dateInput}
        </Form.Item>
    );
}

function NumberInput({ item, infoEdit, field, record, parentFormItemLayout, layout, formId }) {
    let config = getConfig(item, infoEdit, parentFormItemLayout, layout);
    let { rules, disabled, label, formItemLayout, maxLength, style, extra, validateTrigger } = config;

    return (
        <Form.Item
            style={style}
            {...field}
            {...formItemLayout}
            label={label}
            maxLength={maxLength}
            fieldKey={getFieldKey(item, field)}
            name={getFieldKey(item, field)}
            rules={rules}
            extra={extra}
            validateTrigger={validateTrigger}
            tooltip={item.tooltip}>
            <InputNumber
                disabled={disabled}
                onChange={e => {
                    let value = e;
                    if (record) {
                        record[item.name] = value;
                    }
                    event.emit("numberInput", { value: value, name: item.name, record: record, formId });
                }}
            />
        </Form.Item>
    );
}

function TableInput({ item, infoEdit, tableDataSource = [], allSelectList, parentFormItemLayout, layout, formId }) {
    const [dataSource, setDataSource] = useState(tableDataSource);
    let [count, setCount] = useState(tableDataSource.length || 0);

    let config = getConfig(item, infoEdit, parentFormItemLayout, layout);
    let { size, tableLayout, operationConfig, hasIndex, formItemLayout } = config;
    let tableInputItemLayout = { labelCol: { span: 0 }, wrapperCol: { span: 24 } };
    formItemLayout = {
        labelCol: item.labelCol || tableInputItemLayout.labelCol,
        wrapperCol: item.wrapperCol || tableInputItemLayout.wrapperCol,
    };
    let maxWidth = 0;
    const handleDelete = deleteIndex => {
        let newData = dataSource.filter((value, index, arr) => {
            return index !== deleteIndex;
        });
        setDataSource(newData);
    };

    const handleSplitDelete = (deleteIndex, record) => {
        dataSource.splice(deleteIndex, record.rowSpan);
        setDataSource([...dataSource]);
    };

    function handleSplit(index, record) {
        let slice = dataSource.slice(index, index + record.rowSpan);
        let newSliceData = JSON.parse(JSON.stringify(slice));
        let countTemp = count;
        newSliceData.map(value => {
            countTemp += 1;
            value.isFromSplit = true;
            value.isSplit = false;
            value.key = countTemp;
            //删除除了文本控件的字段内容，不然拆行会带上旧数据
            item.columns.forEach(column => {
                if (column.type !== "text" && column.type !== "defaultText") {
                    delete value[column.name];
                }
            });
            return value;
        });
        dataSource.splice(index + record.rowSpan, 0, ...newSliceData);
        setCount(countTemp);
        setDataSource([...dataSource]);
    }

    useEffect(() => {
        event.on(`clearTable-${item.name}-${item.key}`, desc => {
            setDataSource([]);
        });
        event.on(`setTableDataSource-${item.name}-${item.key}`, source => {
            if (`${item.name}-${item.key}` === source.tableName) {
                setDataSource(source.data);
                setCount(source.data.length);
            }
        });
        return () => {
            event.off("clearTable");
            event.off(`setTableDataSource-${item.name}-${item.key}`);
        };
    }, []);

    const handleAdd = () => {
        const newData = {
            key: count,
        };
        item.columns.forEach(it => {
            let key = it["name"];
            newData[key] = "";
        });
        setCount(++count);
        setDataSource([...dataSource, newData]);
    };
    if (item.customStr && typeof item.customStr != "object") {
        Object.assign(item, JSON.parse(item.customStr));
    }

    const columns = item.columns.map((column, index) => {
        maxWidth += column.width;
        return {
            title: column.title,
            dataIndex: column.name,
            width: column.width,
            render: column.render
                ? column.render
                : (text, record, index) => {
                      let Control = formItems[column.type];
                      let field = {
                          name: record.key,
                          key: record.key,
                          isListField: true,
                          fieldKey: record.key,
                          listname: item.name,
                      };
                      let child;
                      if (column.customStr) {
                          Object.assign(column, JSON.parse(column.customStr));
                      }
                      if (Control) {
                          let ControlItem = { value: text, ...column };
                          child = (
                              <Control
                                  item={ControlItem}
                                  infoEdit={infoEdit}
                                  field={field}
                                  record={record}
                                  key={index}
                                  selectList={allSelectList[column.name]}
                                  parentFormItemLayout={formItemLayout}
                                  layout={layout}
                                  formId={formId}
                              />
                          );
                      } else {
                          child = <div>{text}</div>;
                      }
                      let rowSpan = 1;
                      if (column.isSplit) {
                          rowSpan = record.rowSpan;
                      }
                      return {
                          children: child,
                          props: {
                              rowSpan: rowSpan,
                          },
                      };
                  },
        };
    });

    function renderOperation(text, record, index) {
        let child = (
            <Space wrap>
                {item.renderOperation && item.renderOperation(text, record, index)}
                {item.delete && ( //普通删除，一般用于增行的删除
                    <Button
                        type="link"
                        onClick={() => {
                            if (item.handleDelete) {
                                item.handleDelete(record).then(() => {
                                    handleDelete(index);
                                });
                            } else {
                                handleDelete(index);
                            }
                        }}>
                        删除
                    </Button>
                )}
                {item.split &&
                    record.isSplit && ( //表格有拆行类型并且该行能拆行
                        <Button type="link" onClick={() => handleSplit(index, record)}>
                            拆行
                        </Button>
                    )}
                {item.split &&
                    record.isFromSplit && ( //拆行删除：是拆出的行数据
                        <Button type="link" onClick={() => handleSplitDelete(index, record)}>
                            删除
                        </Button>
                    )}
            </Space>
        );
        let rowSpan = 1;
        if (item.split) {
            rowSpan = record.rowSpan;
        }
        return {
            children: child,
            props: {
                rowSpan: rowSpan,
            },
        };
    }

    function hasOperation(item) {
        return item.delete || item.split || item.renderOperation;
    }

    if (hasOperation(item)) {
        let operationColumn = {
            title: "操作",
            dataIndex: "操作",
            width: 100,
            align: "center",
            ...operationConfig,
            render: (text, record, index) => {
                return renderOperation(text, record, index);
            },
        };
        columns.push(operationColumn);
        maxWidth += operationColumn.width;
    }
    if (hasIndex) {
        let indexColumn = {
            title: "序号",
            width: 100,
            align: "center",
            render: (text, record, index) => {
                return `${index + 1}`;
            },
        };
        columns.splice(0, 0, indexColumn);
        maxWidth += indexColumn.width;
    }
    return (
        <>
            <Row style={{ width: "100%" }} className={"row-table"}>
                <Table
                    size={size || "small"}
                    scroll={{
                        x: maxWidth,
                    }}
                    tableLayout={tableLayout || "auto"}
                    bordered
                    dataSource={dataSource}
                    columns={columns}
                    rowClassName="editable-row"
                    pagination={false}
                    name={item.name}
                />
            </Row>
            {item.addRow && (
                <Row style={{ width: "100%" }}>
                    <Button
                        onClick={handleAdd}
                        type="primary"
                        style={{
                            marginTop: 16,
                        }}>
                        增行
                    </Button>
                </Row>
            )}
        </>
    );
}

function CascaderControl({ item, infoEdit, field, record, selectList, parentFormItemLayout, layout, formId }) {
    let config = getConfig(item, infoEdit, parentFormItemLayout, layout);
    let { rules, formValueByLabel, style, disabled, formItemLayout, extra, validateTrigger } = config;

    function onChange(value) {}

    function filter(inputValue, path) {
        return path.some(option => option.areaName.toLowerCase().indexOf(inputValue.toLowerCase()) > -1);
    }

    const addressArr = [];
    if (item.provinceValue && item.provinceValue !== "") {
        addressArr.push(item.provinceValue);
        if (item.cityValue && item.cityValue !== "") {
            addressArr.push(item.cityValue);
            if (item.areaValue && item.areaValue !== "") {
                addressArr.push(item.areaValue);
            }
        }
    }
    return (
        <Form.Item
            style={style}
            label={item.label}
            {...formItemLayout}
            fieldKey={getFieldKey(item, field)}
            name={getFieldKey(item, field)}
            rules={rules}
            extra={extra}
            validateTrigger={validateTrigger}
            tooltip={item.tooltip}>
            <Cascader
                disabled={disabled}
                fieldNames={{
                    label: "areaName",
                    value: formValueByLabel ? "areaName" : "areaCode",
                    children: "children",
                }}
                value={addressArr}
                options={selectList || item.list || []}
                onChange={onChange}
                showSearch={{ filter }}
                changeOnSelect
                placeholder={"选择地址"}
            />
        </Form.Item>
    );
}

export function Container(props) {
    return props.children;
}

function RenderContainer({ item, field, infoEdit, record, parentFormItemLayout, layout, formId }) {
    let config = getConfig(item, infoEdit, parentFormItemLayout, layout);
    let { rules, label, disabled, style, formItemLayout, extra, validateTrigger } = config;
    return (
        <Form.Item
            style={style}
            {...field}
            {...formItemLayout}
            label={label}
            fieldKey={getFieldKey(item, field)}
            name={getFieldKey(item, field)}
            rules={rules}
            extra={extra}
            validateTrigger={validateTrigger}
            tooltip={item.tooltip}>
            <FormRender item={item} disabled={disabled} name={getFieldKey(item, field)} formId={formId} />
        </Form.Item>
    );
}

function getFieldKey(item, field) {
    let fieldKey;
    if (field) {
        if (field.listname) {
            if (Object.prototype.toString.call(field.listname) === "[object Array]") {
                fieldKey = [...field.listname, field.fieldKey, item.name];
            } else {
                fieldKey = [field.listname, field.fieldKey, item.name];
            }
        } else {
            fieldKey = [field.name, item.name];
        }
    } else {
        fieldKey = item.name;
    }
    return fieldKey;
}

function CheckboxSelect({ item, infoEdit, field, record, selectList, parentFormItemLayout, layout, formId }) {
    let [refresh, setRefresh] = useState(0);
    let config = getConfig(item, infoEdit, parentFormItemLayout, layout);
    let { rules, idKey, valueKey, disabled, together, style, formItemLayout, extra, validateTrigger } = config;
    let optionKey = valueKey ? valueKey : "name";
    let optionValue = idKey ? idKey : "id";
    return (
        <Form.Item
            style={style}
            {...field}
            {...formItemLayout}
            label={item.label}
            fieldKey={getFieldKey(item, field)}
            name={getFieldKey(item, field)}
            rules={rules}
            extra={extra}
            validateTrigger={validateTrigger}
            tooltip={item.tooltip}>
            <Checkbox.Group
                onChange={checkedValue => {
                    if (record) {
                        record[item.name] = checkedValue;
                    }
                    event.emit("checkboxSelectChange", {
                        value: checkedValue,
                        name: item.name,
                        record: record,
                        formId,
                    });
                    setRefresh(++refresh);
                }}
                disabled={disabled}>
                {(selectList || item.list || []).map((it, index) => {
                    return (
                        <Checkbox key={index} value={it[optionValue]} data={it}>
                            {together ? `${it.id}:${it[optionKey]}` : it[optionKey]}
                        </Checkbox>
                    );
                })}
            </Checkbox.Group>
        </Form.Item>
    );
}

function RadioSelect({ item, infoEdit, field, record, selectList, parentFormItemLayout, layout, formId }) {
    let [refresh, setRefresh] = useState(0);
    let config = getConfig(item, infoEdit, parentFormItemLayout, layout);
    let { rules, idKey, valueKey, disabled, together, style, formItemLayout, extra, validateTrigger } = config;
    let optionKey = valueKey ? valueKey : "name";
    let optionValue = idKey ? idKey : "id";
    const radioSelectView = connect(
        <Radio.Group
            onChange={e => {
                if (record) {
                    record[item.name] = e.target.value;
                }
                event.emit("radioSelectChange", { value: e.target, name: item.name, record: record, formId });
                setRefresh(++refresh);
            }}
            disabled={disabled}>
            {(selectList || item.list || []).map((it, index) => {
                return (
                    <Radio key={index} value={it[optionValue]} data={it}>
                        {together ? `${it.id}:${it[optionKey]}` : it[optionKey]}
                    </Radio>
                );
            })}
        </Radio.Group>,
        config,
        mapReadPretty({
            component: PreviewSelect,
            componentProps: {
                labelKey: optionKey,
                valueKey: optionValue,
                dataSource: selectList || item.list || [],
            },
        }),
    );
    return (
        <Form.Item
            style={style}
            {...field}
            label={item.label}
            {...formItemLayout}
            fieldKey={getFieldKey(item, field)}
            name={getFieldKey(item, field)}
            rules={rules}
            extra={extra}
            validateTrigger={validateTrigger}
            tooltip={item.tooltip}>
            {radioSelectView}
        </Form.Item>
    );
}

function FormList({ item, infoEdit, field, allSelectList, parentFormItemLayout, layout, formId }) {
    let config = getConfig(item, infoEdit, parentFormItemLayout, layout);
    let { minCount, style, rules, disabled, pattern } = config;
    let listName = getFieldKey(item, field);
    return (
        <Form.List name={listName} style={style} rules={rules}>
            {(fields, { add, remove }, { errors }) => (
                <>
                    {fields.map((field, index) => (
                        <div className="edit-list-group" key={field.key}>
                            <Space align="baseline" wrap>
                                {item.children.map((column, index) => {
                                    let Control = formItems[column.type];
                                    let child;
                                    if (Control) {
                                        child = (
                                            <Control
                                                item={column}
                                                infoEdit={infoEdit}
                                                key={index}
                                                selectList={allSelectList[column.name]}
                                                field={field}
                                                parentFormItemLayout={parentFormItemLayout}
                                                layout={layout}
                                                formId={formId}
                                            />
                                        );
                                    } else {
                                        child = null;
                                    }
                                    return child;
                                })}
                            </Space>
                            {pattern !== FormPatternTypes.readOnly &&
                            pattern !== FormPatternTypes.readPretty &&
                            field.key >= (minCount || 0) ? (
                                <span onClick={() => remove(field.name)} className={"edit-list-close"}>
                                    <DeleteOutlined />
                                </span>
                            ) : null}
                        </div>
                    ))}
                    {pattern !== FormPatternTypes.readPretty && pattern !== FormPatternTypes.readOnly && (
                        <Button type="dashed" disabled={disabled} onClick={() => add()} block>
                            <PlusOutlined /> 新增
                        </Button>
                    )}

                    <Form.ErrorList errors={errors} />
                </>
            )}
        </Form.List>
    );
}

function Uploader({ item, infoEdit, field, parentFormItemLayout, layout, formId }) {
    let config = getConfig(item, infoEdit, parentFormItemLayout, layout);
    let {
        rules,
        maxCount,
        disabled,
        extra,
        listType,
        style,
        accept,
        action,
        fileKey,
        className,
        onlyPreview,
        showUploadList,
        onPreview,
        formItemLayout,
        validateTrigger,
        size,
        uploadPrefix,
    } = config;
    const uploaderView = connect(
        <UploadFile
            className={className}
            disabled={disabled}
            accept={accept}
            maxCount={maxCount}
            size={size}
            action={action || "//dante-img.oss-cn-hangzhou.aliyuncs.com"}
            listType={listType || "picture-card"}
            fileKey={fileKey}
            onlyPreview={onlyPreview}
            showUploadList={showUploadList}
            onPreview={onPreview}
            uploadPrefix={uploadPrefix}
        />,
        config,
        mapReadPretty({
            component: PreviewUpload,
            componentProps: { className, showUploadList, listType, onPreview },
        }),
    );
    return (
        <Form.Item
            style={style}
            label={item.label}
            rules={rules}
            extra={!onlyPreview && extra}
            tooltip={item.tooltip}
            validateTrigger={validateTrigger}
            {...formItemLayout}
            fieldKey={getFieldKey(item, field)}
            name={getFieldKey(item, field)}>
            {uploaderView}
        </Form.Item>
    );
}

function InputAndSelect({ item, infoEdit, field, record, selectList, parentFormItemLayout, layout, formId }) {
    let config = getConfig(item, infoEdit, parentFormItemLayout, layout);
    let { rules, style, formItemLayout, onSearch } = config;
    config.placeholder = config.placeholder || `请输入${item.label}`;
    return (
        <Form.Item
            style={style}
            {...field}
            label={item.label}
            {...formItemLayout}
            fieldKey={getFieldKey(item, field)}
            name={getFieldKey(item, field)}
            rules={rules}>
            <InputSelect
                selectList={selectList}
                config={config}
                onInputSelectChange={(value, option, changeSource) => {
                    event.emit("inputAndSelect", {
                        value: { input: value, option: option, changeSource },
                        name: item.name,
                        record: record,
                        formId,
                    });
                }}
                onSearch={onSearch}
            />
        </Form.Item>
    );
}

export const formItems = {
    container: Container,
    textInput: TextInput,
    text: Text,
    "single-select": CommonSelect,
    "group-select": GroupSelect,
    "json-select": CommonSelect,
    textarea: Textarea,
    number: NumberInput,
    dateInput: DateInput,
    tableInput: TableInput,
    address: CascaderControl,
    renderContainer: RenderContainer,
    radioSelect: RadioSelect,
    checkboxSelect: CheckboxSelect,
    formList: FormList,
    uploader: Uploader,
    "input-and-select": InputAndSelect,
};
